import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CommentThread, VersionDetail, VersionStatus, WorkspaceMember } from '@katsquare/types';
import { Card, SheetBackdrop, Spinner, useSettledAfterMount } from '../../components/ui';
import {
  addComment,
  createThread,
  deleteThread,
  getVersion,
  listMembers,
  listThreads,
  reopenThread,
  resolveThread,
} from '../../lib/api';
import { type DrawTool, type PendingAnchor, ProofViewer } from './ProofViewer';
import { MentionComposer, renderWithMentions } from './MentionComposer';
import { useToast } from '../../components/Toast';
import { IconChevronDown, IconComment, IconCheck, IconReopen } from '../../components/icons';
import { timeAgo } from '../../lib/format';

/** Phone-sized layout, where the comments panel is a full-screen overlay. */
const isPhone = () => window.matchMedia('(max-width: 767px)').matches;

/** Render pipeline badge for a proof version. */
export function StatusBadge({ status }: { status: VersionStatus }) {
  const map: Record<VersionStatus, { label: string; cls: string }> = {
    UPLOADING: { label: 'Uploading', cls: 'bg-neutral-100 text-neutral-600' },
    PROCESSING: { label: 'Rendering', cls: 'bg-amber-100 text-amber-700' },
    READY: { label: 'Ready', cls: 'bg-emerald-100 text-emerald-700' },
    FAILED: { label: 'Failed', cls: 'bg-red-100 text-red-700' },
  };
  const { label, cls } = map[status];
  return <span className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>{label}</span>;
}

/** Loads a version (polling while it renders), then mounts the viewer + comments. */
export function VersionPane({
  versionId,
  tool,
  onSelectTool,
}: {
  versionId: string;
  tool: DrawTool | null;
  onSelectTool: (tool: DrawTool) => void;
}) {
  const queryClient = useQueryClient();
  const [pageIndex, setPageIndex] = useState(0);
  const [pending, setPending] = useState<PendingAnchor | null>(null);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(null);
  const [commentsOpen, setCommentsOpen] = useState(() => {
    // On phones start collapsed so the viewer is usable; the panel opens as a
    // full-screen overlay on tap. Desktop honours the saved preference.
    if (typeof window !== 'undefined' && isPhone()) return false;
    return localStorage.getItem('ksq-comments-open') !== '0';
  });
  const toggleComments = (open: boolean) => {
    setCommentsOpen(open);
    localStorage.setItem('ksq-comments-open', open ? '1' : '0');
  };
  // Reveal the panel without touching the saved collapse preference — used when
  // an action is meaningless while it's hidden (placing a mark, opening a thread).
  const revealComments = () => setCommentsOpen(true);

  const version = useQuery({
    queryKey: ['version', versionId],
    queryFn: () => getVersion(versionId),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'READY' || s === 'FAILED' ? false : 1500;
    },
  });
  const ready = version.data?.status === 'READY';
  const threads = useQuery({
    queryKey: ['threads', versionId],
    queryFn: () => listThreads(versionId),
    enabled: ready,
  });

  // Live updates: refetch this version's threads/review whenever anyone changes them.
  useEffect(() => {
    if (!ready) return;
    const es = new EventSource(`/api/versions/${versionId}/events`, { withCredentials: true });
    es.addEventListener('review', () => {
      queryClient.invalidateQueries({ queryKey: ['threads', versionId] });
      queryClient.invalidateQueries({ queryKey: ['review', versionId] });
      queryClient.invalidateQueries({ queryKey: ['audit'] });
      // Keeps the sidebar's open-comment badge live; that query has no poll of its own.
      queryClient.invalidateQueries({ queryKey: ['projectBoard'] });
    });
    return () => es.close();
  }, [ready, versionId, queryClient]);

  const changePage = (i: number) => {
    setPageIndex(i);
    setPending(null);
    setSelectedThreadId(null);
  };
  // Selecting a thread from the (all-pages) comment list jumps to its page —
  // unlike changePage, the selection survives so the viewer can pan to it.
  const selectThreadAt = (id: string, page: number) => {
    if (page !== pageIndex) {
      setPageIndex(page);
      setPending(null);
    }
    setSelectedThreadId(id);
  };
  // A new mark is only half the interaction — the composer that turns it into a
  // comment lives in the panel, which is collapsed by default on phones. Placing
  // an anchor (tap, pin, box, freehand) therefore has to open the panel, or the
  // tap looks like it did nothing at all.
  const placeAnchor = (anchorType: PendingAnchor['anchorType'], anchor: PendingAnchor['anchor']) => {
    setPending({ anchorType, anchor });
    revealComments();
  };
  // Same for tapping an existing marker: the thread it selects is in the panel.
  const openThread = (id: string | null) => {
    setSelectedThreadId(id);
    if (id) revealComments();
  };
  // Toggling the armed tool off (in the header) also drops any draft anchor.
  useEffect(() => {
    if (!tool) setPending(null);
  }, [tool]);

  if (version.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (version.isError) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center text-sm text-red-500">
        {(version.error as Error).message}
      </div>
    );
  }

  const v = version.data;
  if (v.status === 'FAILED') {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <Card className="max-w-md p-6 text-center">
          <p className="mb-1 font-semibold text-red-600">Rendering failed</p>
          <p className="text-sm text-neutral-500">{v.error ?? 'Unknown error.'}</p>
        </Card>
      </div>
    );
  }
  if (v.status !== 'READY' || v.pages.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-sm text-neutral-500">
        <Spinner />
        <span>{v.status === 'UPLOADING' ? 'Uploading…' : 'Rendering pages…'}</span>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0">
      <div className="min-h-0 flex-1">
        <ProofViewer
          version={v}
          pageIndex={pageIndex}
          onPageChange={changePage}
          threads={threads.data ?? []}
          tool={tool}
          pending={pending}
          onPlaceAnchor={placeAnchor}
          selectedThreadId={selectedThreadId}
          onSelectThread={openThread}
          onSelectTool={onSelectTool}
        />
      </div>
      {commentsOpen ? (
        <>
          {/* Tap anywhere on the page to minimise the sheet (phones only — on
              desktop the panel is a permanent sidebar). Sits above the viewer's
              controls, the review bar and the app header (all z-30 or below) so
              one tap always means "dismiss", and below the nav drawer and menus
              (z-40+) so those still win when they're open. The faint wash is
              what tells you the tap will work — kept light because the thing
              underneath is a colour proof. */}
          <SheetBackdrop onDismiss={() => setCommentsOpen(false)} />
          <CommentsPanel
            version={v}
            pageIndex={pageIndex}
            threads={threads.data ?? []}
            threadsLoading={threads.isPending && ready}
            pending={pending}
            onCancelPending={() => {
              setPending(null);
              // Abandoning the mark on a phone hands the page back rather than
              // leaving the sheet sitting there.
              if (isPhone()) setCommentsOpen(false);
            }}
            onPosted={() => setPending(null)}
            selectedThreadId={selectedThreadId}
            onSelectThread={selectThreadAt}
            onCollapse={() => toggleComments(false)}
          />
        </>
      ) : (
        <CollapsedCommentsRail
          openCount={(threads.data ?? []).filter((t) => t.status === 'OPEN').length}
          onExpand={() => toggleComments(true)}
        />
      )}
    </div>
  );
}

/**
 * Thin rail shown when the comments panel is collapsed. The whole rail — icon,
 * count and the "Comments" label — is the target, so tapping the word on a phone
 * brings the sheet back up rather than only the small chevron doing it.
 */
function CollapsedCommentsRail({ openCount, onExpand }: { openCount: number; onExpand: () => void }) {
  return (
    <aside
      onClick={onExpand}
      title="Show comments"
      className="flex w-10 shrink-0 cursor-pointer flex-col items-center border-l border-neutral-200 bg-white py-2 hover:bg-neutral-50"
    >
      <button
        type="button"
        onClick={onExpand}
        title="Show comments"
        aria-label="Show comments"
        className="flex h-8 w-8 items-center justify-center rounded text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
      >
        <IconChevronDown className="h-4 w-4 rotate-90" />
      </button>
      <div className="mt-2 flex flex-col items-center gap-2">
        <IconComment className="h-4 w-4 text-neutral-400" />
        {openCount > 0 && (
          <span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold text-white">
            {openCount}
          </span>
        )}
      </div>
      <span className="mt-2 text-[11px] font-medium text-neutral-400 [writing-mode:vertical-rl]">
        Comments
      </span>
    </aside>
  );
}

type Filter = 'all' | 'open' | 'resolved';
type Sort = 'position' | 'newest' | 'oldest';
type Scope = 'document' | 'page';

function CommentsPanel({
  version,
  pageIndex,
  threads,
  threadsLoading,
  pending,
  onCancelPending,
  onPosted,
  selectedThreadId,
  onSelectThread,
  onCollapse,
}: {
  version: VersionDetail;
  pageIndex: number;
  threads: CommentThread[];
  threadsLoading: boolean;
  pending: PendingAnchor | null;
  onCancelPending: () => void;
  onPosted: () => void;
  selectedThreadId: string | null;
  onSelectThread: (id: string, pageIndex: number) => void;
  onCollapse: () => void;
}) {
  const queryClient = useQueryClient();
  // The sheet mounts under the finger that opened it, so its close controls
  // ignore anything arriving in that same gesture.
  const settled = useSettledAfterMount();
  const collapse = () => {
    if (settled()) onCollapse();
  };
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>('position');
  // The whole document by default — comments on other pages stay visible (and
  // clicking one jumps there), instead of the list emptying out per page.
  const [scope, setScope] = useState<Scope>('document');
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['threads', version.id] });
    queryClient.invalidateQueries({ queryKey: ['projectBoard'] });
  };

  const members = useQuery({ queryKey: ['members', version.id], queryFn: () => listMembers(version.id) });
  const pageId = version.pages[pageIndex]?.id;

  const create = useMutation({
    mutationFn: (vars: { body: string; mentions: string[] }) =>
      createThread(pageId as string, {
        anchorType: (pending as PendingAnchor).anchorType,
        anchor: (pending as PendingAnchor).anchor,
        body: vars.body,
        mentions: vars.mentions,
      }),
    onSuccess: (thread) => {
      onPosted();
      onSelectThread(thread.id, thread.pageIndex);
      invalidate();
    },
  });

  const inScope = scope === 'page' ? threads.filter((t) => t.pageIndex === pageIndex) : threads;
  const visible = inScope
    .filter((t) => (filter === 'all' ? true : filter === 'open' ? t.status === 'OPEN' : t.status === 'RESOLVED'))
    .sort((a, b) => {
      if (sort === 'newest') return b.createdAt.localeCompare(a.createdAt);
      if (sort === 'oldest') return a.createdAt.localeCompare(b.createdAt);
      // Document order: by page, then down the page.
      return a.pageIndex - b.pageIndex || a.anchor.y - b.anchor.y || a.anchor.x - b.anchor.x;
    });
  const openCount = inScope.filter((t) => t.status === 'OPEN').length;

  // Clicking a mark on the page has to land on a card you can actually see. If
  // the active filter hides that thread (clicking a resolved mark while showing
  // "Open"), fall back to "All" instead of highlighting nothing.
  useEffect(() => {
    if (!selectedThreadId || filter === 'all') return;
    const t = threads.find((x) => x.id === selectedThreadId);
    if (!t) return;
    const shown = filter === 'open' ? t.status === 'OPEN' : t.status === 'RESOLVED';
    if (!shown) setFilter('all');
  }, [selectedThreadId, filter, threads]);

  // Markers are numbered within their own page, so the list must match.
  const numberOf = (t: CommentThread) =>
    threads.filter((x) => x.pageIndex === t.pageIndex).indexOf(t) + 1;

  return (
    <aside
      data-tour="pdf-comments"
      // Phones: a bottom sheet over the lower half of the screen, so the page
      // stays visible (and tappable, to dismiss). Height comes from .ksq-sheet.
      // Desktop: the fixed sidebar.
      className="ksq-sheet fixed inset-x-0 bottom-0 z-[36] flex flex-col rounded-t-2xl border-t border-neutral-200 bg-white shadow-[0_-8px_30px_rgba(0,0,0,0.18)] md:static md:z-auto md:h-auto md:max-h-none md:w-80 md:shrink-0 md:rounded-none md:border-l md:border-t-0 md:shadow-none"
    >
      {/* Sheet grab-bar. Looks like a handle, so it should behave like one —
          tapping it closes the sheet, same as the header chevron. */}
      <button
        type="button"
        onClick={collapse}
        aria-label="Hide comments"
        className="flex justify-center pb-0.5 pt-2 md:hidden"
      >
        <span className="h-1 w-9 rounded-full bg-neutral-300" />
      </button>
      <div className="flex items-center gap-2 border-b border-neutral-200 px-4 py-2.5">
        <h2 className="text-sm font-semibold text-neutral-900">
          Comments <span className="font-normal text-neutral-400">· {openCount} open</span>
        </h2>
        <button
          type="button"
          onClick={() => setScope((s) => (s === 'document' ? 'page' : 'document'))}
          title={
            scope === 'document'
              ? 'Showing every page — click to show only this page'
              : `Showing page ${pageIndex + 1} only — click to show every page`
          }
          className="ml-auto shrink-0 rounded border border-neutral-200 px-1.5 py-0.5 text-[11px] font-medium text-neutral-600 hover:bg-neutral-100"
        >
          {scope === 'document' ? 'All pages' : 'This page'}
        </button>
        <button
          type="button"
          onClick={collapse}
          title="Hide comments"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
        >
          <IconChevronDown className="h-4 w-4 -rotate-90" />
        </button>
      </div>

      {pending && (
        <div className="border-b border-neutral-200 p-3">
          <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-neutral-400">
            New {pending.anchorType.toLowerCase()} comment
          </p>
          <MentionComposer
            members={members.data ?? []}
            multiline
            autoFocus
            submitLabel="Comment"
            placeholder="Add a comment… use @ to mention"
            busy={create.isPending}
            onCancel={() => {
              if (settled()) onCancelPending();
            }}
            onSubmit={(body, mentions) => create.mutate({ body, mentions })}
          />
          {create.isError && <p className="mt-1 text-xs text-red-500">{(create.error as Error).message}</p>}
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-neutral-200 px-3 py-2">
        {(['all', 'open', 'resolved'] as Filter[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`rounded px-2 py-1 text-xs font-medium capitalize ${
              filter === f ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:bg-neutral-100'
            }`}
          >
            {f}
          </button>
        ))}
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value as Sort)}
          title="Sort comments"
          className="ml-auto rounded border border-neutral-200 bg-white px-1.5 py-1 text-xs text-neutral-600 outline-none focus:border-neutral-400"
        >
          <option value="position">Document order</option>
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
        </select>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {threadsLoading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : visible.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-neutral-400">
            {inScope.length > 0
              ? 'No comments match this filter.'
              : scope === 'page'
                ? 'No comments on this page yet.'
                : 'No comments on this PDF yet.'}
          </p>
        ) : (
          <ul>
            {visible.map((t) => (
              <ThreadCard
                key={t.id}
                thread={t}
                members={members.data ?? []}
                index={numberOf(t)}
                pageLabel={
                  scope === 'document' && version.pages.length > 1 ? `Page ${t.pageIndex + 1}` : null
                }
                offPage={t.pageIndex !== pageIndex}
                selected={t.id === selectedThreadId}
                onSelect={() => onSelectThread(t.id, t.pageIndex)}
                onChanged={invalidate}
              />
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}

function ThreadCard({
  thread,
  members,
  index,
  pageLabel,
  offPage,
  selected,
  onSelect,
  onChanged,
}: {
  thread: CommentThread;
  members: WorkspaceMember[];
  index: number;
  /** "Page 3" chip, shown when the list spans the whole document. */
  pageLabel: string | null;
  /** True when this thread lives on a page other than the one on screen. */
  offPage: boolean;
  selected: boolean;
  onSelect: () => void;
  onChanged: () => void;
}) {
  const resolved = thread.status === 'RESOLVED';
  const toast = useToast();
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const addReply = useMutation({
    mutationFn: (vars: { body: string; mentions: string[] }) =>
      addComment(thread.id, { body: vars.body, mentions: vars.mentions }),
    onSuccess: onChanged,
    onError: (e: Error) => toast(e.message),
  });
  const toggle = useMutation({
    mutationFn: () => (resolved ? reopenThread(thread.id) : resolveThread(thread.id)),
    onSuccess: onChanged,
    onError: (e: Error) => toast(e.message),
  });
  const remove = useMutation({
    mutationFn: () => deleteThread(thread.id),
    onSuccess: onChanged,
    onError: (e: Error) => toast(e.message),
  });

  // Scroll into view when selected (e.g. by clicking its marker on the page).
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selected]);

  return (
    <li
      ref={ref}
      onClick={onSelect}
      className={`cursor-pointer border-b border-neutral-100 px-4 py-3 ${
        selected ? 'bg-amber-50 ring-1 ring-inset ring-amber-300' : 'hover:bg-neutral-50'
      }`}
    >
      <div className="mb-1 flex items-center gap-1.5">
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold text-white ${
            resolved ? 'bg-emerald-500' : 'bg-blue-600'
          }`}
        >
          {index}
        </span>
        {pageLabel && (
          <span
            className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${
              offPage ? 'bg-neutral-100 text-neutral-500' : 'bg-blue-50 text-blue-700'
            }`}
            title={offPage ? 'On another page — click to jump there' : 'On the page you are viewing'}
          >
            {pageLabel}
          </span>
        )}
        {resolved && <span className="text-[11px] font-medium text-emerald-600">Resolved</span>}
        {thread.carried && (
          <span
            className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-medium text-violet-700"
            title="Carried forward from the previous round"
          >
            carried
          </span>
        )}
        <div className="ml-auto flex items-center gap-0.5">
          {confirmingDelete ? (
            <>
              <span className="text-[11px] text-red-600">Delete thread?</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmingDelete(false);
                  remove.mutate();
                }}
                className="rounded px-1.5 py-0.5 text-[11px] font-semibold text-red-600 hover:bg-red-50"
                disabled={remove.isPending}
              >
                Delete
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmingDelete(false);
                }}
                className="rounded px-1 text-[11px] text-neutral-400 hover:text-neutral-700"
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  toggle.mutate();
                }}
                className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-emerald-600"
                disabled={toggle.isPending}
                title={resolved ? 'Reopen' : 'Resolve'}
              >
                {resolved ? <IconReopen className="h-4 w-4" /> : <IconCheck className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setConfirmingDelete(true);
                }}
                className="rounded p-1 text-neutral-400 hover:bg-neutral-100 hover:text-red-600"
                disabled={remove.isPending}
                title="Delete thread"
              >
                ✕
              </button>
            </>
          )}
        </div>
      </div>

      {thread.anchorType === 'TEXT' && thread.anchor.quote && (
        <p className="mb-1.5 line-clamp-2 border-l-2 border-amber-400 bg-amber-50 py-0.5 pl-2 text-xs italic text-neutral-600">
          “{thread.anchor.quote}”
        </p>
      )}

      <div className="space-y-1.5">
        {thread.comments.map((c) => (
          <div key={c.id}>
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs font-semibold text-neutral-800">{c.author?.name ?? 'Unknown'}</span>
              {c.authorKind === 'GUEST' && (
                <span
                  className="rounded bg-violet-100 px-1 py-px text-[9px] font-medium uppercase tracking-wide text-violet-700"
                  title="Came in through an external review link — this name is self-declared and unverified"
                >
                  Guest
                </span>
              )}
              <span className="text-[10px] text-neutral-400" title={new Date(c.createdAt).toLocaleString()}>
                {timeAgo(c.createdAt)}
              </span>
            </div>
            <p className="whitespace-pre-wrap text-sm leading-snug text-neutral-700">
              {renderWithMentions(c.body, c.mentions)}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-1.5">
        <MentionComposer
          members={members}
          placeholder="Reply… use @ to mention"
          submitLabel="Send"
          busy={addReply.isPending}
          onSubmit={(body, mentions) => addReply.mutate({ body, mentions })}
        />
      </div>
    </li>
  );
}

